'use client'

import {
  get360Preview,
  getAssetKey,
  getAudioPreview,
  getDetailPath,
  getDetailUrl,
  getFileDownloadUrl,
  getFileFormatLabel,
  getFileResolutionLabel,
  getImagePreview,
  getPanoramaPreview,
  getQueueUrl,
  getResolutionByDimensions,
  getThumbnail,
  getVideoPreview,
  isFootageCrateEffect,
  resolveFileFormat as resolveFileFormatUtil,
} from '@/lib/assets/assets'
import { enqueueBlenderAsset } from '@/lib/assets/blender-queue'
import { sortFormatOptions } from '@/lib/assets/format-priority'
import { sortResolutionOptions } from '@/lib/assets/resolution-score'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Asset, AssetBase, AssetSummary, Resolution } from '@/lib/assets/types'
import { useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { useUserRealtimeContext } from '@/lib/contexts/UserRealtimeContext'
import { toast } from 'sonner'
import api from '@/lib/api/crate-core-api-client'
import { AxiosError } from 'axios'
import { CRATE_CORE_API_BASE_URL, CRATE_LOGIN_URL, DOWNLOAD_HOST } from '@/lib/config'
import {
  generateRegisterUrl,
  generateUpgradeUrl,
  generateVerifyUrl,
  storeIntendedUrl,
  triggerDownload,
} from '@/lib/utils'
import { withDownloadAttributionParams } from '@/lib/attribution/download'
import { presentDownloadRefusal } from '@/lib/download-protection/present-refusal'
import { PRESENTED_REFUSAL_CODES, TURNSTILE_TOKEN_HEADER, targetsCrateCore } from '@/lib/download-protection/refusals'
import { sendDownloadUpgradePromptEvent } from '@/lib/analytics/download-upgrade'

export type FormatOption = {
  label: string
  value: string
}

export type ResolutionOption = {
  label: string
  value: string
}

export type DownloadOption = {
  url: string
  file: Asset['files'][number]
  resolution?: ResolutionOption
  format?: FormatOption
  variationFormat?: FormatOption
  size?: number
  level?: string | number
}

// Reuse your existing constants
// const ALLOWED_CODES = new Set(['OK','NO_CONSUMPTIONS_LEFT','POLICY_REFUSED_CONSUMPTION','NOT_VERIFIED']);
// const CODES_METADATA = { ... } // as in your snippet

type SuccessToastOptions = Parameters<typeof toast.success>[1]

type QueueRequestOpts = {
  /** Full endpoint URL to hit (e.g., portal queue or blender queue) */
  url: string
  /** HTTP method; defaults to GET unless a body is provided (then POST) */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** Bearer token (if required) */
  token?: string | null
  /** Request body; will be JSON.stringify'ed unless it's FormData */
  body?: unknown | FormData
  /** Extra headers to merge */
  headers?: Record<string, string>
  /** Success toast message */
  successMessage?: string
  /** Success toast options */
  successToastOptions?: SuccessToastOptions
}

const ALLOWED_CODES = new Set([
  'OK',
  'NO_CONSUMPTIONS_LEFT',
  'POLICY_REFUSED_CONSUMPTION',
  'NOT_VERIFIED',
  'SUBSCRIPTION_NOT_ACTIVE',
  ...PRESENTED_REFUSAL_CODES,
])

/**
 * Shows crate-core's `SUBSCRIPTION_NOT_ACTIVE` refusal: its message, with the one button its
 * `action` asks for. `ask_owner`, an unknown action, or a payment link without a url gets no
 * button. Returns false for any other body so the caller keeps looking.
 */
function presentSubscriptionNotActive(data: unknown, retry?: () => void | Promise<void>): boolean {
  const body = data as { code?: unknown, message?: unknown, action?: unknown, url?: unknown } | null
  if (!body || body.code !== 'SUBSCRIPTION_NOT_ACTIVE') {
    return false
  }

  const message = typeof body.message === 'string' && body.message
    ? body.message
    : 'Your subscription is not active.'
  const url = typeof body.url === 'string' && body.url ? body.url : undefined

  let action: { label: string, onClick: () => void } | undefined
  if (body.action === 'complete_payment' && url) {
    action = { label: 'Complete payment', onClick: () => { window.location.href = url } }
  } else if (body.action === 'retry') {
    action = { label: 'Try again', onClick: () => { void retry?.() } }
  } else if (body.action === 'resubscribe') {
    action = {
      label: 'Choose a plan',
      onClick: () => { window.location.href = generateUpgradeUrl(window.location.href) },
    }
  }

  toast.error(message, action ? { action } : undefined)
  return true
}

const CODES_METADATA = {
  NO_CONSUMPTIONS_LEFT: {
    msg: 'You hit the download limit of your plan',
    action: 'Upgrade',
    actionLabel: 'Upgrade',
  },
  POLICY_REFUSED_CONSUMPTION: {
    msg: 'Upgrade to PRO to download this asset',
    action: 'Upgrade',
    actionLabel: 'Upgrade',
  },
  NOT_VERIFIED: {
    msg: 'Verify your email to download',
    action: 'Verify',
    actionLabel: 'Verify email',
  },
}

export type DownloadRestriction = 'POLICY_REFUSED_CONSUMPTION' | 'NOT_VERIFIED'

/**
 * What one request for a download URL came back as.
 *
 * The record is deliberately plain: a status, whatever the API answered with, and the
 * `Retry-After` header when there was one. The HTTP client the request was made with stays
 * this hook's business, so an observer never has to know it is axios to read an outcome.
 */
export type DownloadAttemptOutcome = {
  /** The download URL that was requested, as it went on the wire. */
  url: string
  /** The HTTP status, or undefined when the request never got an answer at all. */
  status?: number
  /** True when a download URL came back; false for every failure, refusal included. */
  ok: boolean
  /** The parsed response body, exactly as the API sent it. */
  body?: unknown
  /** The `Retry-After` header, when the answer carried one. */
  retryAfter?: string
}

/**
 * The `Retry-After` header off a response, whichever shape the HTTP client handed it over
 * in. Axios gives plain objects on some paths and a header bag with `get` on others, and a
 * diagnostic log is not worth a crash when it is neither.
 */
function readRetryAfter(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') {
    return undefined
  }

  const bag = headers as { get?: (name: string) => unknown, 'retry-after'?: unknown }

  const value = typeof bag.get === 'function' ? bag.get('retry-after') : bag['retry-after']

  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : undefined
}

interface UseAssetOptions {
  asset?: Asset | AssetSummary | null
  userToken?: string
  onDownloadRestriction?: (restriction: DownloadRestriction) => void
  /**
   * Observes every request this hook makes for a download URL, refusals and retries
   * included, and is called before the outcome is acted on.
   *
   * It exists for diagnostics — the dev download-protection playground logs what the API
   * answered — and changes nothing: the hook presents refusals and errors exactly as it
   * would without one. An observer that throws would break a download that was otherwise
   * fine, so it must not throw.
   */
  onDownloadAttempt?: (outcome: DownloadAttemptOutcome) => void
}

/**
 * This hooks handles various aspects of asset management, including:
 * - Extracting available resolutions and formats from the asset files.
 * - Generating preview URLs for different asset types (image, video, 360, panorama, audio).
 * - Providing download options based on the asset files.
 * - Generating asset detail URLs.
 * - Dispatching download events for analytics or tracking purposes.
 */
export default function useAsset({ asset, userToken, onDownloadRestriction, onDownloadAttempt }: UseAssetOptions) {
  const appEvent$ = useEventEmitterContext()
  const { setDownloadMethod } = useUserRealtimeContext()

  const resolutions: ResolutionOption[] = useMemo(() => {
    if (!asset || !asset.files?.length) {
      return []
    }

    const originalDimensions = 'resolution' in asset.properties ? asset.properties.resolution : undefined

    // One option per distinct raw resolution, labelled from the first file that has it
    const options: ResolutionOption[] = []
    for (const file of asset.files) {
      const value =
        file.properties?.variations && 'resolution' in file.properties.variations
          ? file.properties.variations.resolution
          : undefined
      if (!value || options.some((option) => option.value === value)) {
        continue
      }
      options.push({
        label: getFileResolutionLabel(file, { appendDimensions: true, originalDimensions }) ?? value,
        value,
      })
    }

    return sortResolutionOptions(options)
  }, [asset])

  const assetKey = useMemo(() => {
    return asset ? getAssetKey(asset) : ''
  }, [asset])

  const thumbnail = useMemo(() => {
    if (!asset) {
      return null
    }

    return getThumbnail(asset)
  }, [asset])

  const imagePreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getImagePreview(asset)
  }, [asset])

  const videoPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getVideoPreview(asset)
  }, [asset])

  const asset360Preview = useMemo(() => {
    if (!asset) {
      return null
    }

    return get360Preview(asset)
  }, [asset])

  const panoramaPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getPanoramaPreview(asset)
  }, [asset])

  const audioPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getAudioPreview(asset)
  }, [asset])

  const resolveVariationFormat = useCallback((file: Asset['files'][number]): string | null => {
    const formatVariation: string | null =
      'format' in (file.properties?.variations ?? {}) ? file.properties?.variations?.format : null

    return formatVariation ?? null
  }, [])

  const resolveFileFormat = useCallback(
    (file: Asset['files'][number]): string | null => resolveFileFormatUtil(file),
    []
  )

  /**
   * Resolve the variation resolution from a given asset file.
   * @param file
   */
  const resolveVariationResolution = useCallback((file: Asset['files'][number]): string | null => {
    return file.properties?.variations && 'resolution' in file.properties.variations
      ? file.properties.variations.resolution
      : null
  }, [])

  /**
   * Get unique file formats from the asset files.
   * @param files The array of asset files to extract formats from.
   * @returns An array of unique format options.
   */
  const getFileFormats = useCallback(
    (files: Asset['files']): FormatOption[] => {
      // One option per distinct format key, labelled from the first file that has it
      const mapped: FormatOption[] = []
      for (const file of files) {
        const format = resolveFileFormat(file)
        if (!format || mapped.some((option) => option.value === format)) {
          continue
        }
        mapped.push({ label: getFileFormatLabel(file, format), value: format })
      }

      return sortFormatOptions(mapped)
    },
    [resolveFileFormat]
  )

  const formats: FormatOption[] = useMemo(() => {
    if (!asset || !asset.files?.length) {
      return []
    }

    return getFileFormats(asset.files ?? [])
  }, [asset, getFileFormats])

  /**
   * Get available formats for a given resolution value.
   * This is useful when the user selects a specific resolution,
   * and we want to update the available formats accordingly.
   * @param resolutionValue The resolution value to filter formats by.
   * @returns An array of format options available for the given resolution.
   */
  const getFormatsForResolution = useCallback(
    (resolutionValue: string): FormatOption[] => {
      if (!asset || !asset.files?.length) {
        return []
      }

      const files = asset.files.filter((file) => {
        const fileResolution = resolveVariationResolution(file)
        const fileFormat = resolveFileFormat(file)
        return fileResolution === resolutionValue && !!fileFormat
      })

      return getFileFormats(files)
    },
    [asset, getFileFormats, resolveFileFormat, resolveVariationResolution]
  )

  // Current asset resolution (if any)
  const resolution: Resolution | null = useMemo(() => {
    if (!asset || !asset.properties || !('resolution' in asset.properties)) {
      return null
    }

    return getResolutionByDimensions(asset.properties.resolution ?? '')
  }, [asset])

  const is4K = useMemo(() => {
    if (!asset) {
      return false
    }

    return isFootageCrateEffect(asset) && resolution?.id === '4k'
  }, [asset, resolution?.id])

  const isLightBackground = useMemo(() => asset?.properties.background === 'light', [asset])

  // Generate asset detail link using legacy URL.
  // E.g.: https://vfx.productioncrate.com/video-effects/FootageCrate-4K_World_Breaker_Shockwave_Angled_8
  const legacyAssetDetailLink = useMemo(() => {
    if (!asset) {
      return null
    }

    return getDetailUrl(asset)
  }, [asset])

  const downloadOptions: DownloadOption[] = useMemo(() => {
    if (!asset || !asset.files?.length) {
      return []
    }

    return asset.files.map((file) => {
      const resolution: string | undefined = resolveVariationResolution(file) ?? undefined
      const format: string | undefined = resolveFileFormat(file) ?? undefined
      const variationFormat: string | undefined = resolveVariationFormat(file) ?? undefined
      const url = getFileDownloadUrl({ file })

      return {
        url,
        file: file,
        resolution: resolution
          ? {
              label:
                getFileResolutionLabel(file, {
                  appendDimensions: true,
                  originalDimensions: 'resolution' in asset.properties ? asset.properties.resolution : undefined,
                }) ?? resolution,
              value: resolution,
            }
          : undefined,
        format: format
          ? {
              label: getFileFormatLabel(file, format),
              value: format ?? '',
            }
          : undefined,
        variationFormat: variationFormat
          ? {
              label: getFileFormatLabel(file, variationFormat),
              value: variationFormat ?? '',
            }
          : undefined,
        size: file.properties.size ?? undefined,
        level: file.level ?? undefined,
      }
    })
  }, [asset, resolveFileFormat, resolveVariationFormat, resolveVariationResolution])

  // Asset detail URL
  const assetDetailUrl = useMemo(() => {
    if (!asset) {
      return ''
    }

    return getDetailPath(asset) ?? ''
  }, [asset])

  const extractDownloadParams = useCallback((downloadUrl?: string): Record<string, string> | undefined => {
    if (!downloadUrl) return

    try {
      const url = new URL(downloadUrl)
      const params: Record<string, string> = {}

      url.searchParams.forEach((value, key) => {
        if (key !== 'request_token' && key !== 'anonymous_id' && key !== 'session_id') {
          params[key] = value
        }
      })

      return params
    } catch {
      return
    }
  }, [])

  const dispatchAssetDownloadEvent = useCallback(
    (downloadUrl?: string) => {
      appEvent$.emit({
        type: 'asset:download',
        payload: {
          ...asset,
          downloadParams: extractDownloadParams(downloadUrl),
        },
      })
    },
    [appEvent$, asset, extractDownloadParams]
  )

  /**
   * Generic download-queue enqueuer.
   * It encapsulates:
   *  - JSON-safe parsing
   *  - 403 + invalid/unknown `code` handling
   *  - Friendly toasts with Upgrade/Verify redirections
   *  - Success toast + console debug
   */
  const enqueueDownloadRequest = useCallback(async (opts: QueueRequestOpts): Promise<void> => {
    const { url, token, body, headers = {}, successMessage = 'Downloading...', successToastOptions } = opts

    const method: QueueRequestOpts['method'] = opts.method ?? (body ? 'POST' : 'GET')

    // Build headers: JSON by default unless FormData
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
    const reqHeaders: Record<string, string> = {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
      ...(!isFormData && body ? { 'Content-Type': 'application/json' } : {}),
    }

    // Build request init
    const init: RequestInit = {
      method,
      headers: reqHeaders,
      body: body ? (isFormData ? (body as FormData) : JSON.stringify(body)) : undefined,
    }

    const res = await fetch(url, init)

    // JSON-safe parsing
    let parsed: any = null
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) {
      try {
        parsed = await res.json()
      } catch {
        // ignore JSON parse errors, 'parsed' stays null
      }
    }

    const code = parsed?.code as string | undefined

    // If 403 and code is missing or not allowed -> hard error
    if (res.status === 403 && (!code || !ALLOWED_CODES.has(code))) {
      throw new Error('403/forbidden code')
    }

    if (!res.ok) {
      // A download-protection refusal explains itself, so it must not fall through to the
      // generic toast below. The queue paths cannot hand over a retry, so a delayed member
      // is told when the wait is over and starts the download again themselves.
      if (presentDownloadRefusal(parsed)) {
        throw new Error(`HTTP ${res.status}`)
      }

      // No retry to hand over here either: "Try again" means clicking download again.
      if (presentSubscriptionNotActive(parsed)) {
        throw new Error(`HTTP ${res.status}`)
      }

      // If we got a known code, show a tailored toast with action
      const meta = code ? CODES_METADATA[code as keyof typeof CODES_METADATA] : undefined

      if (code === 'POLICY_REFUSED_CONSUMPTION' && onDownloadRestriction) {
        onDownloadRestriction(code)
      } else if (meta) {
        const isUpgrade = meta.action === 'Upgrade'
        if (isUpgrade) {
          sendDownloadUpgradePromptEvent('download_upgrade_prompt_shown', {
            surface: 'download_toast',
            is_logged_in: Boolean(token),
            refusal_code: code,
          })
        }

        toast.error(meta.msg, {
          action: {
            label: meta.actionLabel,
            onClick: () => {
              if (isUpgrade) {
                sendDownloadUpgradePromptEvent('download_upgrade_prompt_clicked', {
                  surface: 'download_toast',
                  is_logged_in: Boolean(token),
                  refusal_code: code,
                })
              }

              const redirectTo = window.location.href
              if (meta.action === 'Upgrade') {
                window.location.href = generateUpgradeUrl(redirectTo)
              } else {
                window.location.href = generateVerifyUrl(redirectTo)
              }
            },
          },
        })
      } else {
        // Fallback toast for unknown/absent codes
        toast.error('Could not start download.')
      }

      // Enforce your "invalid code" contract
      if (!code || !ALLOWED_CODES.has(code)) {
        throw new Error('Invalid API code')
      }
      throw new Error(`HTTP ${res.status}`)
    }

    console.debug('Added to download queue:', parsed ?? res)
    toast.success(successMessage, successToastOptions)
  }, [onDownloadRestriction])

  // Downloads
  const [isDownloading, setIsDownloading] = useState(false)

  /**
   * Hands one attempt's outcome to the observer, if there is one, without letting a broken
   * observer take a download down with it.
   *
   * Kept in a ref so that passing a fresh inline callback on every render does not rebuild
   * `fetchDownloadUrl` and, through it, every download callback that depends on it.
   */
  const onDownloadAttemptRef = useRef(onDownloadAttempt)
  useEffect(() => {
    onDownloadAttemptRef.current = onDownloadAttempt
  }, [onDownloadAttempt])

  const reportDownloadAttempt = useCallback((outcome: DownloadAttemptOutcome) => {
    try {
      onDownloadAttemptRef.current?.(outcome)
    } catch (observerError) {
      console.error('A download-attempt observer threw; the download is unaffected:', observerError)
    }
  }, [])

  /**
   * Fetch the actual download URL from the API.
   * @param url
   * @param challengeToken A Turnstile token from a passed challenge, sent so the API lets
   *   this download through. Only a download being retried after `CHALLENGE_REQUIRED` has one.
   * @returns The download URL as a string.
   * @throws Will throw an error if the download URL cannot be fetched.
   */
  const fetchDownloadUrl = useCallback(async (url: string, challengeToken?: string): Promise<string> => {
    const attributedUrl = withDownloadAttributionParams(url)

    // Every request from here carries the member's JWT, and a solved Turnstile token when
    // there is one. Not every download URL reaching here came from our own routing — one
    // path reads it out of a query parameter — so a request pointed anywhere but crate-core
    // is refused before it is made rather than handed those credentials.
    if (!targetsCrateCore(attributedUrl, CRATE_CORE_API_BASE_URL)) {
      console.warn('Refusing to request a download URL outside crate-core:', url)
      throw new Error('Refusing to request a download URL outside crate-core')
    }

    try {
      const response = await api.get<{ download_url?: string, url?: string }>(attributedUrl, {
        headers: challengeToken ? { [TURNSTILE_TOKEN_HEADER]: challengeToken } : undefined,
      })

      const downloadUrl = response.data.download_url ?? response.data.url

      reportDownloadAttempt({
        url: attributedUrl,
        status: response.status,
        ok: Boolean(downloadUrl),
        body: response.data,
        retryAfter: readRetryAfter(response.headers),
      })

      if (!downloadUrl) {
        throw new Error('No download URL found')
      }
      return downloadUrl
    } catch (error) {
      // A thrown "No download URL found" has already been reported above; reporting it a
      // second time here would put the same attempt in the log twice.
      if (error instanceof AxiosError) {
        reportDownloadAttempt({
          url: attributedUrl,
          status: error.response?.status,
          ok: false,
          body: error.response?.data,
          retryAfter: readRetryAfter(error.response?.headers),
        })
      }

      throw error
    }
  }, [reportDownloadAttempt])

  const openPaymentMethodUpdate = useCallback(async () => {
    try {
      const response = await api.post<{ url?: string }>('/subscriptions/payment-method-update')

      if (!response.data.url) {
        throw new Error('Payment-method update URL was not returned')
      }

      window.location.href = response.data.url
    } catch (paymentMethodError) {
      console.error('Unable to open the payment-method update page:', paymentMethodError)
      toast.error('Unable to open the payment-method update page. Please try again later.')
    }
  }, [])

  /**
   * Handle errors that occur while fetching the download URL.
   * @param error The error object caught during the fetch operation.
   */
  const handleFetchDownloadUrlError = useCallback((error: unknown, retry?: (challengeToken?: string) => void | Promise<void>) => {
    console.error('Error fetching download URL:', error instanceof AxiosError ? error.response?.status : 'Request failed')
    let message = 'An error occurred while fetching the download URL.'

    // If is authentication error, redirect to log in
    if (error instanceof AxiosError && error.response?.status === 401) {
      const redirectTo = window.location.href
      storeIntendedUrl(redirectTo) // Store the intended URL to redirect after login
      window.location.href = `${CRATE_LOGIN_URL}`
      return
    }

    // Download protection answers with its own codes; those explain themselves to the
    // member, and a delayed download resumes through `retry` once the wait is over.
    if (error instanceof AxiosError && presentDownloadRefusal(error.response?.data, retry)) {
      return
    }

    if (error instanceof AxiosError && presentSubscriptionNotActive(error.response?.data, retry)) {
      return
    }

    if (error instanceof AxiosError && error.response?.data?.code === 'PAYMENT_PAST_DUE') {
      toast.error('We could not process your payment. Update your payment method to continue downloading premium assets.', {
        action: {
          label: 'Update payment method',
          onClick: () => {
            void openPaymentMethodUpdate()
          },
        },
      })
      return
    }

    // Handle FEATURE_NOT_FOUND code
    if (error instanceof AxiosError && error.response?.data?.code === 'FEATURE_NOT_FOUND') {
      message = 'Your plan does not support downloads. Please upgrade your plan.'
      sendDownloadUpgradePromptEvent('download_upgrade_prompt_shown', {
        surface: 'download_toast',
        is_logged_in: Boolean(userToken),
        refusal_code: 'FEATURE_NOT_FOUND',
      })
      toast.error(message, {
        action: {
          label: 'Upgrade',
          onClick: () => {
            sendDownloadUpgradePromptEvent('download_upgrade_prompt_clicked', {
              surface: 'download_toast',
              is_logged_in: Boolean(userToken),
              refusal_code: 'FEATURE_NOT_FOUND',
            })
            const redirectTo = window.location.href
            window.location.href = generateRegisterUrl(redirectTo)
          },
        },
      })
      return
    }

    // Handle NOT_VERIFIED code
    if (error instanceof AxiosError && error.response?.data?.code === 'NOT_VERIFIED') {
      toast.error('Verify your email to download', {
        action: {
          label: 'Verify email',
          onClick: () => {
            const redirectTo = window.location.href
            window.location.href = generateVerifyUrl(redirectTo)
          },
        },
      })
      return
    }

    // Handle POLICY_REFUSED_CONSUMPTION code
    if (error instanceof AxiosError && error.response?.data?.code === 'POLICY_REFUSED_CONSUMPTION') {
      if (onDownloadRestriction) {
        onDownloadRestriction('POLICY_REFUSED_CONSUMPTION')
        return
      }

      message = 'Your plan does not allow this download. Please upgrade your plan.'
      sendDownloadUpgradePromptEvent('download_upgrade_prompt_shown', {
        surface: 'download_toast',
        is_logged_in: Boolean(userToken),
        refusal_code: 'POLICY_REFUSED_CONSUMPTION',
      })
      toast.error(message, {
        action: {
          label: 'Upgrade',
          onClick: () => {
            sendDownloadUpgradePromptEvent('download_upgrade_prompt_clicked', {
              surface: 'download_toast',
              is_logged_in: Boolean(userToken),
              refusal_code: 'POLICY_REFUSED_CONSUMPTION',
            })
            const redirectTo = window.location.href
            window.location.href = generateRegisterUrl(redirectTo)
          },
        },
      })
      return
    }

    // Handle NO_CONSUMPTIONS_LEFT code
    if (error instanceof AxiosError && error.response?.data?.code === 'NO_CONSUMPTIONS_LEFT') {
      message = 'You have no downloads left. Please upgrade your plan or wait until your limit resets.'
      sendDownloadUpgradePromptEvent('download_upgrade_prompt_shown', {
        surface: 'download_toast',
        is_logged_in: Boolean(userToken),
        refusal_code: 'NO_CONSUMPTIONS_LEFT',
      })
      toast.error(message, {
        action: {
          label: 'Upgrade',
          onClick: () => {
            sendDownloadUpgradePromptEvent('download_upgrade_prompt_clicked', {
              surface: 'download_toast',
              is_logged_in: Boolean(userToken),
              refusal_code: 'NO_CONSUMPTIONS_LEFT',
            })
            const redirectTo = window.location.href
            window.location.href = generateRegisterUrl(redirectTo)
          },
        },
      })
      return
    }

    // Fallback
    message = error instanceof AxiosError ? (error.response?.data?.message ?? message) : message
    toast.error(message)
  }, [onDownloadRestriction, openPaymentMethodUpdate, userToken])

  /**
   * Holds the latest `startBrowserDownloadFromFile` so a refused download can retry itself
   * without the callback having to name itself inside its own definition.
   */
  const startBrowserDownloadRef = useRef<
    ((file: Asset['files'][number], options?: { force?: boolean; challengeToken?: string }) => Promise<void>)
    | null
  >(null)

  const startBrowserDownloadFromFile = useCallback(
    async (
      file: Asset['files'][number],
      { force = false, challengeToken }: { force?: boolean; challengeToken?: string } = {}
    ) => {
      if (isDownloading && !force) return
      setIsDownloading(true)

      const url = getFileDownloadUrl({ file })

      try {
        const downloadUrl = await fetchDownloadUrl(url, challengeToken)
        dispatchAssetDownloadEvent(downloadUrl)
        triggerDownload(downloadUrl)
        toast.success('Your download has started')
      } catch (error) {
        handleFetchDownloadUrlError(error, (retryChallengeToken) =>
          startBrowserDownloadRef.current?.(file, { force: true, challengeToken: retryChallengeToken })
        )
      } finally {
        setIsDownloading(false)
      }
    },
    [
      dispatchAssetDownloadEvent,
      fetchDownloadUrl,
      handleFetchDownloadUrlError,
      isDownloading,
    ]
  )

  useEffect(() => {
    startBrowserDownloadRef.current = startBrowserDownloadFromFile
  }, [startBrowserDownloadFromFile])

  /* =========================
   * USAGE IN YOUR DISPATCHERS
   * ========================= */

  /**
   * PORTAL (GET)
   */
  const dispatchPortalDownload = useCallback(
    async (file: Asset['files'][number]) => {
      try {
        const result = await enqueueDownloadRequest({
          url: withDownloadAttributionParams(getQueueUrl({ file, queueType: 'portal' })),
          method: 'GET',
          token: userToken,
          successMessage: 'Downloading through portal',
          successToastOptions: {
            duration: 10_000,
            action: {
              label: 'Disable',
              onClick: () => {
                void setDownloadMethod('browser')
                void startBrowserDownloadFromFile(file, { force: true })
              },
            },
          },
        })
        return result
      } catch (err) {
        console.error(err)
      }
    },
    [enqueueDownloadRequest, setDownloadMethod, startBrowserDownloadFromFile, userToken]
  )

  /**
   * BLENDER ADD-ON (POST)
   */
  const dispatchBlenderAddonDownload = useCallback(
    async (asset: AssetBase, _file?: Asset['files'][number]): Promise<boolean> => {
      if (!userToken || !asset) {
        toast.error('You must be logged in to download through the Blender addon')
        return false
      }
      if (!('slug' in asset && asset.slug)) {
        toast.error('Could not download through blender addon')
        return false
      }

      try {
        await enqueueBlenderAsset(asset.slug)
        toast.success('Downloading through blender addon')
        return true
      } catch (error) {
        handleFetchDownloadUrlError(error)
        return false
      }
    },
    [handleFetchDownloadUrlError, userToken]
  )

  /**
   * Download an asset file given its URL.
   * This function handles the download process, including error handling
   * for various scenarios such as authentication errors and plan limitations.
   * @param file The asset file to be downloaded.
   * @param platform The platform from which the download is initiated.
   * Can be 'web', 'portal', or 'blender'.
   * @param onSuccess Optional callback to execute upon successful download initiation.
   */
  const download = useCallback(
    async (
      file: Asset['files'][number],
      { platform, onSuccess }: { platform: 'web' | 'portal' | 'blender'; onSuccess?: () => Promise<void> } = {
        platform: 'web',
        onSuccess: async () => {},
      }
    ) => {
      if (isDownloading) return // Prevent multiple downloads at the same time
      setIsDownloading(true)

      const url = getFileDownloadUrl({ file })

      // If the download host config is defined, ensure we're in the right domain
      // If not, redirect to the download host with the necessary params
      if (!!DOWNLOAD_HOST) {
        console.debug('Checking cross-domain download requirements...')

        // Ensure we're in the download domain
        const currentHost = window.location.href
        const downloadHost = DOWNLOAD_HOST

        if (downloadHost && !currentHost.includes(new URL(downloadHost).host)) {
          // Redirect to the same page, adding download params
          // This should trigger CaptureAssetDownload to handle the download
          console.debug('Redirecting to download host:', downloadHost)

          const currentUrl = new URL(currentHost)
          currentUrl.host = downloadHost.replace(/^https?:\/\//, '') // Remove protocol if present
          currentUrl.searchParams.set('asset', asset?.uuid ?? '') // Set download parameters for redirect
          currentUrl.searchParams.set('file', file.uuid)
          currentUrl.searchParams.set('download', withDownloadAttributionParams(url))
          currentUrl.searchParams.set('platform', platform)
          window.location.href = currentUrl.toString()
          return
        }
      }

      try {
        if (platform === 'web') {
          await startBrowserDownloadFromFile(file, { force: true })
        } else if (platform === 'portal') {
          await dispatchPortalDownload(file)
        } else if (platform === 'blender' && asset) {
          if (!(await dispatchBlenderAddonDownload(asset, file))) return
        }

        await onSuccess?.()
      } catch (error) {
        handleFetchDownloadUrlError(error)
      } finally {
        setIsDownloading(false)
      }
    },
    [
      asset,
      dispatchBlenderAddonDownload,
      dispatchPortalDownload,
      handleFetchDownloadUrlError,
      isDownloading,
      startBrowserDownloadFromFile,
    ]
  )

  return {
    assetKey,
    assetDetailUrl,
    resolutions,
    formats,
    getFormatsForResolution,
    downloadOptions,
    dispatchAssetDownloadEvent,
    dispatchPortalDownload,
    dispatchBlenderAddonDownload,
    thumbnail,
    imagePreview,
    videoPreview,
    asset360Preview,
    panoramaPreview,
    audioPreview,
    resolution,
    is4K,
    isLightBackground,
    legacyAssetDetailLink,
    download,
    fetchDownloadUrl,
    handleFetchDownloadUrlError,
    isDownloading,
  }
}
